import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  FileText,
  ArrowRight,
  ShieldCheck,
  Zap,
  Layout,
  Clock,
  Settings,
  Download,
  Search,
  ArrowLeft,
  X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageHeader from '../components/PageHeader';

const StepCard = ({ number, title, description }) => (
  <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <div className="w-10 h-10 bg-[#141414] text-white rounded-full flex items-center justify-center font-bold mb-4">
      {number}
    </div>
    <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const FeatureItem = ({ icon: Icon, title, description }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center">
      <Icon size={24} />
    </div>
    <div>
      <h4 className="font-bold text-gray-900 mb-1">{title}</h4>
      <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
    </div>
  </div>
);

const ToolCard = ({ title, description, badge }) => (
  <div className="bg-white border border-gray-100 p-6 rounded-2xl shadow-sm hover:border-blue-200 transition-colors">
    {badge && (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 mb-3">
        {badge}
      </span>
    )}
    <h3 className="font-bold text-gray-900 mb-2">{title}</h3>
    <p className="text-sm text-gray-600 leading-relaxed">{description}</p>
  </div>
);

const NextPDFPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('organizar');

  const flowSteps = useMemo(() => t('nextpdf.flow.steps', { returnObjects: true }), [t]);
  const uiItems = useMemo(() => {
    const items = t('nextpdf.flow.ui_items', { returnObjects: true });
    const icons = [Search, Layout, Clock, FileText];
    return items.map((item, idx) => ({ ...item, icon: icons[idx] }));
  }, [t]);

  const catalog = useMemo(() => t('nextpdf.catalog', { returnObjects: true }), [t]);
  const currentCategory = catalog[activeTab];

  return (
    <div className="relative min-h-screen bg-transparent font-inter">
      <PageHeader />

      <main className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <button
            type="button"
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#141414] mb-8 transition font-medium"
          >
            <ArrowLeft size={16} />
            {t('common.back')}
          </button>

          <div className="text-center mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-bold bg-blue-50 text-blue-700 mb-6 border border-blue-100">
              {t('nextpdf.badge')}
            </span>
            <h1 className="text-4xl md:text-6xl font-extrabold text-[#141414] mb-6 tracking-tight">
              {t('nextpdf.title')}
            </h1>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-10 leading-relaxed">
              {t('nextpdf.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <a
                href="#herramientas"
                className="bg-[#141414] text-white hover:bg-gray-900 font-bold py-3.5 px-10 rounded-full transition shadow-lg"
              >
                {t('nextpdf.cta_tools')}
              </a>
              <Link
                to="/apps"
                className="bg-white border border-gray-200 text-[#141414] hover:bg-gray-50 font-bold py-3.5 px-10 rounded-full transition"
              >
                {t('common.all_apps')}
              </Link>
            </div>
            <p className="mt-6 text-sm text-gray-500 italic">
              {t('nextpdf.disclaimer')}
            </p>
          </div>

          <section className="mb-24 bg-gray-50 rounded-[2.5rem] p-8 md:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full blur-3xl opacity-50 -mr-32 -mt-32" />
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-white text-gray-500 border border-gray-100 mb-4 uppercase tracking-widest">
                  {t('nextpdf.flow.badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-6">
                  {t('nextpdf.flow.title')}
                </h2>
                <p className="text-gray-600 text-lg mb-10 leading-relaxed">
                  {t('nextpdf.flow.desc')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {uiItems.map((item) => (
                    <FeatureItem
                      key={item.title}
                      icon={item.icon}
                      title={item.title}
                      description={item.desc}
                    />
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {flowSteps.map((step, idx) => (
                  <StepCard
                    key={step.title}
                    number={idx + 1}
                    title={step.title}
                    description={step.desc}
                  />
                ))}
              </div>
            </div>
          </section>

          <section id="herramientas" className="mb-24 scroll-mt-32">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
                {t('nextpdf.catalog_title')}
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                {t('nextpdf.catalog_desc')}
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-2 mb-10">
              {Object.keys(catalog).map((key) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold transition-all ${
                    activeTab === key
                      ? 'bg-[#141414] text-white shadow-md scale-105'
                      : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {catalog[key].label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {currentCategory.tools.map((tool) => (
                <ToolCard
                  key={tool.title}
                  title={tool.title}
                  description={tool.description}
                  badge={activeTab === 'convertir' && tool.title.includes('Word') ? t('nextpdf.pdf_word_extra')[0] : null}
                />
              ))}
            </div>
          </section>

          <section className="mb-24 bg-[#141414] rounded-[2.5rem] p-8 md:p-16 text-white relative overflow-hidden">
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/20 rounded-full blur-3xl -ml-48 -mb-48" />
            <div className="relative z-10 flex flex-col lg:flex-row gap-12 items-center">
              <div className="lg:w-1/2">
                <span className="inline-block px-3 py-1 rounded-full text-xs font-bold bg-white/10 text-white/70 border border-white/10 mb-4 uppercase tracking-widest">
                  {t('nextpdf.privacy.badge')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  {t('nextpdf.privacy.title')}
                </h2>
                <p className="text-white/70 text-lg mb-10 leading-relaxed">
                  {t('nextpdf.privacy.desc')}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                  {t('nextpdf.privacy.items', { returnObjects: true }).map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <ShieldCheck className="text-blue-400 flex-shrink-0" size={24} />
                      <div>
                        <h4 className="font-bold mb-1">{item.title}</h4>
                        <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:w-1/2 w-full">
                <div className="bg-white text-gray-900 rounded-3xl p-8 shadow-2xl">
                  <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100">
                    <h4 className="font-bold text-lg">{t('nextpdf.preview_label', { tool: currentCategory.tools[0].title })}</h4>
                    <div className="flex gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400" />
                      <div className="w-3 h-3 rounded-full bg-yellow-400" />
                      <div className="w-3 h-3 rounded-full bg-green-400" />
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="flex items-center gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100">
                      <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-blue-600">
                        <FileText size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="h-2 w-24 bg-gray-200 rounded mb-2" />
                        <div className="h-2 w-16 bg-gray-100 rounded" />
                      </div>
                      <X size={16} className="text-gray-300" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 rounded-2xl border border-gray-100">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('nextpdf.ui_mockup.quality')}</div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">{t('nextpdf.ui_mockup.quality_mid')}</span>
                          <Settings size={14} className="text-gray-400" />
                        </div>
                      </div>
                      <div className="p-4 rounded-2xl border border-gray-100">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{t('nextpdf.ui_mockup.output')}</div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold">{t('nextpdf.ui_mockup.output_1')}</span>
                          <Download size={14} className="text-gray-400" />
                        </div>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100">
                      <div className="flex items-center gap-3 mb-2">
                        <Zap size={16} className="text-blue-600" />
                        <span className="text-xs font-bold text-blue-700 uppercase tracking-widest">{t('nextpdf.ui_mockup.summary')}</span>
                      </div>
                      <div className="text-sm font-bold text-blue-900 mb-1">{t('nextpdf.ui_mockup.result')}</div>
                      <div className="text-xs text-blue-700/70 leading-relaxed">{t('nextpdf.ui_mockup.result_desc')}</div>
                    </div>

                    <button className="w-full bg-[#141414] text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-2 group transition-all hover:bg-gray-800">
                      {t('nextpdf.ui_mockup.btn_start')}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-2xl font-bold text-[#141414] mb-4">{t('nextpdf.footer_title')}</h2>
            <p className="text-gray-600 mb-8">{t('nextpdf.footer_desc')}</p>
            <Link
              to="/apps"
              className="inline-flex items-center gap-2 bg-gray-100 text-[#141414] hover:bg-gray-200 font-bold py-3 px-8 rounded-full transition"
            >
              <ArrowLeft size={18} />
              {t('common.all_apps')}
            </Link>
          </section>
        </div>
      </main>
    </div>
  );
};

export default NextPDFPage;
